import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { AnyValue } from "@/lib/rowTypes";

export interface CreditAccount {
  id: string;
  user_id: string;
  credit_limit: number;
  current_balance: number;
  minimum_payment: number;
  payment_due_date: string | null;
  payment_status: string;
  credit_score: number;
  score_history: Array<{ date: string; score: number; reason: string }>;
  payment_history: Array<Record<string, AnyValue>>;
  status: string;
  unlock_condition: string | null;
  unlocked_at: string | null;
  created_at: string;
  updated_at: string;
  autopay_enabled: boolean;
  autopay_source_account_id: string | null;
  autopay_amount_type: string;
  autopay_custom_amount: number | null;
  autopay_next_run_date: string | null;
  autopay_last_run_date: string | null;
  autopay_last_amount: number | null;
  autopay_status: string;
}

export type ScoreTier = "poor" | "fair" | "good" | "excellent";

export function getScoreTier(score: number): ScoreTier {
  if (score >= 750) return "excellent";
  if (score >= 700) return "good";
  if (score >= 650) return "fair";
  return "poor";
}

export function getScoreTierLabel(tier: ScoreTier): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

export function getScoreTip(tier: ScoreTier): string {
  switch (tier) {
    case "excellent": return "Outstanding! Keep up your responsible habits.";
    case "good": return "Solid score. Pay on time and keep utilization low.";
    case "fair": return "Pay on time to build your score higher.";
    case "poor": return "Focus on paying balances down and never missing payments.";
  }
}

export function useCredit() {
  const { user } = useAuth();
  const [credit, setCredit] = useState<CreditAccount | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCredit = useCallback(async () => {
    if (!user) { setCredit(null); setLoading(false); return; }
    await supabase.rpc("ensure_credit_account");
    const { data } = await supabase
      .from("credit_accounts")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();
    setCredit((data as AnyValue) || null);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchCredit(); }, [fetchCredit]);

  const makePayment = async (amount: number) => {
    const { error } = await supabase.rpc("make_credit_payment", { p_amount: amount });
    if (error) throw new Error(error.message);
    await fetchCredit();
  };

  const unlock = async () => {
    const { error } = await supabase.rpc("unlock_credit_account");
    if (error) throw new Error(error.message);
    await fetchCredit();
  };

  const purchase = async (amount: number, description: string) => {
    const { error } = await supabase.rpc("credit_purchase", {
      p_amount: amount,
      p_description: description,
    });
    if (error) throw new Error(error.message);
    await fetchCredit();
  };

  const configureAutopay = async (
    enabled: boolean,
    sourceAccountType: string = "checking",
    amountType: string = "minimum",
    customAmount?: number
  ) => {
    const { error } = await supabase.rpc("configure_autopay", {
      p_enabled: enabled,
      p_source_account_type: sourceAccountType,
      p_amount_type: amountType,
      p_custom_amount: customAmount || null,
    });
    if (error) throw new Error(error.message);
    await fetchCredit();
  };

  const availableCredit = credit ? credit.credit_limit - credit.current_balance : 0;
  const utilization = credit && credit.credit_limit > 0
    ? Math.round((credit.current_balance / credit.credit_limit) * 100)
    : 0;
  const isLocked = !credit || credit.status === "locked";
  const scoreTier = credit ? getScoreTier(credit.credit_score) : "fair";

  return {
    credit,
    loading,
    isLocked,
    availableCredit,
    utilization,
    scoreTier,
    makePayment,
    unlock,
    purchase,
    configureAutopay,
    refetch: fetchCredit,
  };
}
